/**
 * Service for Banker Reconciliation CRUD operations
 * Handles banker_recons and banker_recon_line_items tables
 */

import { supabase } from '@/utils/supabase';
import { readReconFeeAccounting, type ReconFeeSource } from '@/utils/banker-recon/feePaymentLedger';
import { createComponentLogger } from '@/utils/logger';
import type {
  BankerRecon,
  BankerReconLineItem,
  CreateBankerReconInput,
  CreateLineItemInput,
  UpdateBankerReconInput,
  BankerReconStatus
} from '@/types/banker-recon';
import {
  EMPTY_FHA_UFMIP_SOURCE,
  isFhaLoanType,
  type FhaUfmipCaptureStatus,
  type FhaUfmipSourceSnapshot
} from '@/utils/banker-recon/fhaUfmip';
import { createFromBankerRecon, deleteServicingLoan, precacheServicingForms } from '@/services/servicingService';
import { relinkPayoutPaymentAllocations } from '@/services/payoutPaymentService';
import {
  buildRevenueReportLoanProjection,
  type RevenueReportLoan
} from '@/utils/banker-recon/revenueReportProjection';
import {
  BANKER_EXTRACTOR_VERSION,
  type BankerDocumentType,
  type AutoFillExtractionCache,
  type AutoFillReviewMark,
  type ReconDocumentAutoFill
} from '@/components/banker-recon/types';
import {
  flattenExtractionForFeedback,
  diffExtractionVsFinal,
  ownedMissedCategories,
  buildFinalFeedbackItems
} from '@/components/banker-recon/autoFillFeedback';

const log = createComponentLogger('BankerReconService', 'database');

type CompensationSettingsRow = {
  banked_compensation: number | null;
  banked_override_bps: number | null;
  override_recipient_id: string | null;
  override_recipient?: {
    first_name: string | null;
    last_name: string | null;
  } | null;
};

// ============================================================================
// BANKER RECON CRUD
// ============================================================================

/**
 * Get a single banker recon by ID with line items
 */
export async function getBankerRecon(id: string): Promise<BankerRecon | null> {
  const timer = log.startTimer('getBankerRecon');

  const { data, error } = await supabase
    .from('banker_recons')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    log.error('Failed to get banker recon', { id }, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.endTimer(timer, { success: true, found: !!data });
  return data as BankerRecon | null;
}

/**
 * Get banker recon by encompass loan ID for an organization
 */
export async function getBankerReconByLoan(
  organizationId: string,
  encompassLoanId: string,
  options: { throwOnError?: boolean } = {}
): Promise<BankerRecon | null> {
  const timer = log.startTimer('getBankerReconByLoan');

  const { data, error } = await supabase
    .from('banker_recons')
    .select('*, override_recipient:override_recipient_id(first_name, last_name)')
    .eq('organization_id', organizationId)
    .eq('encompass_loan_id', encompassLoanId)
    .maybeSingle();

  if (error) {
    log.error('Failed to get banker recon by loan', { organizationId, encompassLoanId }, error);
    log.endTimer(timer, { success: false });
    // Strict callers must not mistake a failed read for "no recon".
    if (options.throwOnError) {
      throw new Error('The reconciliation could not be loaded.');
    }
    return null;
  }

  log.endTimer(timer, { success: true, found: !!data });
  return data as BankerRecon | null;
}

const FHA_UFMIP_CAPTURE_STATUSES = new Set<FhaUfmipCaptureStatus>([
  'not_read',
  'not_applicable',
  'ok',
  'incomplete',
  'invalid',
  'conflict',
  'read_error'
]);

type CanonicalFhaUfmipRow = {
  deleted_from_encompass: boolean | null;
  trans_details_loan_type: string | null;
  mi_funding_fee_total_amount: number | string | null;
  mi_funding_fee_financed_amount: number | string | null;
  fha_ufmip_remitted_date: string | null;
  fha_ufmip_remitted_by: string | null;
  fha_ufmip_remitted_amount: number | string | null;
  fha_ufmip_verification_code: string | null;
  fha_ufmip_capture_status: string | null;
  fha_ufmip_source_signature: string | null;
  fha_ufmip_capture_details: Record<string, unknown> | null;
  fha_ufmip_last_attempted_at: string | null;
  fha_ufmip_captured_at: string | null;
};

function canonicalNullableMoney(value: number | string | null): number | null {
  if (value === null) {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new Error('Canonical FHA UFMIP contains a non-finite money value');
  }
  return parsed;
}

/**
 * Mandatory source-of-truth read used by both load and save/finalize. Errors,
 * missing/deleted rows, anomalous statuses, and non-finite numerics all throw;
 * callers must never treat an unverified read as an empty UFMIP snapshot.
 */
export async function getCanonicalFhaUfmip(
  organizationId: string,
  encompassLoanId: string
): Promise<FhaUfmipSourceSnapshot> {
  const { data, error } = await supabase
    .from('encompass_loans')
    .select(`
      deleted_from_encompass,
      trans_details_loan_type,
      mi_funding_fee_total_amount,
      mi_funding_fee_financed_amount,
      fha_ufmip_remitted_date,
      fha_ufmip_remitted_by,
      fha_ufmip_remitted_amount,
      fha_ufmip_verification_code,
      fha_ufmip_capture_status,
      fha_ufmip_source_signature,
      fha_ufmip_capture_details,
      fha_ufmip_last_attempted_at,
      fha_ufmip_captured_at
    `)
    .eq('organization_id', organizationId)
    .eq('encompass_loan_id', encompassLoanId)
    .maybeSingle();

  if (error) {
    log.error('Failed to read canonical FHA UFMIP', {
      metadata: { organizationId, encompassLoanId }
    }, error);
    throw new Error(`FHA UFMIP source read failed: ${error.message}`);
  }
  if (!data || data.deleted_from_encompass) {
    throw new Error('Canonical Encompass loan is missing or deleted');
  }

  const row = data as CanonicalFhaUfmipRow;
  const status = row.fha_ufmip_capture_status;
  if (!status || !FHA_UFMIP_CAPTURE_STATUSES.has(status as FhaUfmipCaptureStatus)) {
    throw new Error('Canonical FHA UFMIP has an unknown capture status');
  }

  // Stage A defaults pre-existing rows to not_read. A canonical, explicitly
  // non-FHA loan does not need the six FHA-only fields to be re-synced before
  // operators can continue saving its draft. Null/unknown loan types remain
  // fail-closed because we cannot safely establish non-applicability.
  const canonicalLoanType = row.trans_details_loan_type?.trim() || null;
  if (!canonicalLoanType) {
    throw new Error('Canonical FHA UFMIP applicability cannot be verified because loan type is missing');
  }
  if (!isFhaLoanType(canonicalLoanType)) {
    if (status !== 'not_read' && status !== 'not_applicable') {
      throw new Error(
        `Canonical non-FHA classification conflicts with FHA UFMIP capture status ${status}`
      );
    }
    return {
      ...EMPTY_FHA_UFMIP_SOURCE,
      captureStatus: 'not_applicable',
      sourceSignature: row.fha_ufmip_source_signature,
      captureDetails: row.fha_ufmip_capture_details,
      lastAttemptedAt: row.fha_ufmip_last_attempted_at,
      capturedAt: row.fha_ufmip_captured_at
    };
  }

  if (status === 'not_read' || status === 'read_error') {
    throw new Error(
      status === 'not_read'
        ? 'FHA UFMIP has not been captured from Encompass'
        : 'The latest FHA UFMIP source read failed'
    );
  }

  return {
    premiumAmount: canonicalNullableMoney(row.mi_funding_fee_total_amount),
    financedAmount: canonicalNullableMoney(row.mi_funding_fee_financed_amount),
    remittedDate: row.fha_ufmip_remitted_date,
    remittedBy: row.fha_ufmip_remitted_by,
    remittedAmount: canonicalNullableMoney(row.fha_ufmip_remitted_amount),
    verificationCode: row.fha_ufmip_verification_code,
    captureStatus: status as FhaUfmipCaptureStatus,
    sourceSignature: row.fha_ufmip_source_signature,
    captureDetails: row.fha_ufmip_capture_details,
    lastAttemptedAt: row.fha_ufmip_last_attempted_at,
    capturedAt: row.fha_ufmip_captured_at
  };
}

/**
 * List all banker recons for an organization with optional status filter.
 * Embeds the LO payee's name (lo_user_id → users) for list display.
 */
export async function listBankerRecons(
  organizationId: string,
  status?: BankerReconStatus
): Promise<BankerRecon[]> {
  const timer = log.startTimer('listBankerRecons');

  let query = supabase
    .from('banker_recons')
    .select('*, lo:lo_user_id(first_name, last_name)')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;

  if (error) {
    log.error('Failed to list banker recons', { organizationId, status }, error);
    log.endTimer(timer, { success: false });
    return [];
  }

  log.endTimer(timer, { success: true, count: data?.length });
  return (data || []) as BankerRecon[];
}

// ============================================================================
// SEARCH EXCLUSIONS
// ============================================================================

export interface FinalizedLoanIdsResult {
  ids: string[];
  success: boolean;
}

/**
 * Fetch encompass_loan_ids that have a finalized banker recon.
 * Returns { ids, success } so callers can distinguish "zero finalized" from "fetch failed".
 */
export async function getFinalizedLoanIds(organizationId: string): Promise<FinalizedLoanIdsResult> {
  const { data, error } = await supabase
    .from('banker_recons')
    .select('encompass_loan_id')
    .eq('organization_id', organizationId)
    .eq('status', 'finalized');

  if (error) {
    log.error('Failed to fetch finalized loan IDs', { organizationId }, error);
    return { ids: [], success: false };
  }

  const ids = (data || [])
    .map(r => r.encompass_loan_id)
    .filter((id): id is string => !!id);

  return { ids, success: true };
}

/**
 * Create a new banker reconciliation
 */
export async function createBankerRecon(
  input: CreateBankerReconInput,
  userId?: string
): Promise<BankerRecon | null> {
  const timer = log.startTimer('createBankerRecon');

  const { data, error } = await supabase
    .from('banker_recons')
    .insert({
      ...input,
      status: 'draft',
      created_by: userId,
      snapshot_at: input.loan_snapshot ? new Date().toISOString() : null
    })
    .select()
    .single();

  if (error) {
    log.error('Failed to create banker recon', input, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.info('Created banker recon', { id: data.id, loanNumber: input.loan_number });
  log.endTimer(timer, { success: true });
  return data as BankerRecon;
}

/**
 * Update an existing banker reconciliation
 */
export async function updateBankerRecon(
  input: UpdateBankerReconInput,
  userId?: string,
  options?: {
    /**
     * Conditional update: only apply when the recon's CURRENT status matches
     * (e.g. 'draft'). Protects frozen finalized/pushed rows against stale
     * client state — the update touches zero rows and returns null instead.
     */
    requireStatus?: BankerReconStatus;
  }
): Promise<BankerRecon | null> {
  const timer = log.startTimer('updateBankerRecon');

  const { id, ...updateData } = input;

  let query = supabase
    .from('banker_recons')
    .update({
      ...updateData,
      updated_by: userId,
      updated_at: new Date().toISOString()
    })
    .eq('id', id);
  if (options?.requireStatus) {
    query = query.eq('status', options.requireStatus);
  }
  const { data, error } = await query
    .select()
    .single();

  if (error) {
    log.error('Failed to update banker recon', { id, requireStatus: options?.requireStatus }, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.info('Updated banker recon', { id });
  log.endTimer(timer, { success: true });
  return data as BankerRecon;
}

/**
 * Delete a banker reconciliation (only if in draft status)
 */
export async function deleteBankerRecon(id: string): Promise<boolean> {
  const timer = log.startTimer('deleteBankerRecon');

  // Only allow deletion of drafts
  const { error } = await supabase
    .from('banker_recons')
    .delete()
    .eq('id', id)
    .eq('status', 'draft');

  if (error) {
    log.error('Failed to delete banker recon', { id }, error);
    log.endTimer(timer, { success: false });
    return false;
  }

  log.info('Deleted banker recon', { id });
  log.endTimer(timer, { success: true });
  return true;
}

// ============================================================================
// LINE ITEMS CRUD
// ============================================================================

/**
 * Get all line items for a banker recon
 */
export async function getLineItems(
  bankerReconId: string,
  options: { throwOnError?: boolean } = {}
): Promise<BankerReconLineItem[]> {
  const timer = log.startTimer('getLineItems');

  const { data, error } = await supabase
    .from('banker_recon_line_items')
    .select('*')
    .eq('banker_recon_id', bankerReconId)
    .order('category')
    .order('display_order');

  if (error) {
    log.error('Failed to get line items', { bankerReconId }, error);
    log.endTimer(timer, { success: false });
    // Strict callers must not mistake a failed read for "no line items".
    if (options.throwOnError) {
      throw new Error('The reconciliation line items could not be loaded.');
    }
    return [];
  }

  log.endTimer(timer, { success: true, count: data?.length });
  return (data || []) as BankerReconLineItem[];
}

/**
 * Create a new line item
 */
export async function createLineItem(input: CreateLineItemInput): Promise<BankerReconLineItem | null> {
  const timer = log.startTimer('createLineItem');

  const { data, error } = await supabase
    .from('banker_recon_line_items')
    .insert({
      ...input,
      source: input.source || 'manual'
    })
    .select()
    .single();

  if (error) {
    log.error('Failed to create line item', input, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.info('Created line item', { id: data.id, category: input.category, lineType: input.line_type });
  log.endTimer(timer, { success: true });
  return data as BankerReconLineItem;
}

/**
 * Update a line item
 */
export async function updateLineItem(
  id: string,
  updates: Partial<Omit<BankerReconLineItem, 'id' | 'banker_recon_id' | 'created_at'>>
): Promise<BankerReconLineItem | null> {
  const timer = log.startTimer('updateLineItem');

  const { data, error } = await supabase
    .from('banker_recon_line_items')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    log.error('Failed to update line item', { id }, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.info('Updated line item', { id });
  log.endTimer(timer, { success: true });
  return data as BankerReconLineItem;
}

/**
 * Delete a line item
 */
export async function deleteLineItem(id: string): Promise<boolean> {
  const timer = log.startTimer('deleteLineItem');

  const { error } = await supabase
    .from('banker_recon_line_items')
    .delete()
    .eq('id', id);

  if (error) {
    log.error('Failed to delete line item', { id }, error);
    log.endTimer(timer, { success: false });
    return false;
  }

  log.info('Deleted line item', { id });
  log.endTimer(timer, { success: true });
  return true;
}

/**
 * Bulk upsert line items for a banker recon
 * Uses database RPC for true atomic replacement (delete + insert in single transaction)
 * Prevents doubled totals from partial failures
 */
/**
 * Atomic line-item replacement. Returns the saved rows, or NULL on RPC failure —
 * failure must be distinguishable from a legitimately empty item list, otherwise
 * "clear all rows" + a failed RPC would report success while old rows persist.
 */
export async function bulkUpsertLineItems(
  bankerReconId: string,
  items: Omit<CreateLineItemInput, 'banker_recon_id'>[]
): Promise<BankerReconLineItem[] | null> {
  const timer = log.startTimer('bulkUpsertLineItems');

  // Prepare items as JSONB array for RPC
  const itemsJson = items.map((item, index) => ({
    category: item.category,
    line_type: item.line_type,
    description: item.description,
    amount: item.amount,
    source: item.source || 'manual',
    display_order: item.display_order ?? index,
    reimbursable: item.reimbursable || false,
    nets_to_warehouse_advance: item.nets_to_warehouse_advance ?? null,
    warehouse_retained_amount: item.warehouse_retained_amount ?? 0
  }));

  // Call RPC for atomic replace (delete + insert in single transaction)
  const { data, error } = await supabase.rpc('replace_banker_recon_line_items', {
    p_banker_recon_id: bankerReconId,
    p_items: itemsJson
  });

  if (error) {
    log.error('Failed to replace line items via RPC', { bankerReconId, count: items.length }, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.info('Replaced line items atomically', { bankerReconId, count: data?.length });
  log.endTimer(timer, { success: true, count: data?.length });
  return (data || []) as BankerReconLineItem[];
}

// ============================================================================
// STATUS TRANSITIONS
// ============================================================================

/**
 * Transactionally validate the saved snapshot, create every payout, and move
 * the recon to finalized. Post-commit integrations remain best-effort below.
 */
export async function finalizeRecon(
  id: string,
  expectedUpdatedAt: string,
  expectedUfmipSignature: string | null,
  volumeBonusReviewToken?: string | null
): Promise<{ success: boolean; error?: string; servicingLoanId?: string; servicingError?: string; recon?: BankerRecon }> {
  const timer = log.startTimer('finalizeRecon');
  const { data, error } = await supabase.rpc('finalize_banker_recon_atomic', {
    p_banker_recon_id: id,
    p_expected_updated_at: expectedUpdatedAt,
    p_expected_ufmip_signature: expectedUfmipSignature,
    ...(volumeBonusReviewToken !== undefined ? { p_volume_bonus_review_token: volumeBonusReviewToken } : {})
  });

  if (error || !data) {
    log.error('Atomic banker recon finalization failed', { id }, error ?? undefined);
    log.endTimer(timer, { success: false });
    return {
      success: false,
      error: error?.message || 'Atomic finalization returned no reconciliation'
    };
  }

  const recon = data as BankerRecon;
  log.info('Finalized recon atomically', { id });

  // Best-effort: capture the extraction → finalized-record diff as training data. Runs AFTER the terminal
  // status write and must NEVER fail finalize (or surface as a finalize error). Idempotent on re-finalize.
  try {
    await captureAutoFillFeedback(id);
  } catch (feedbackErr) {
    log.warn('captureAutoFillFeedback failed (non-blocking)', { id }, feedbackErr as Error);
  }

  // Step 3.5: Relink immutable payment allocations after re-finalization.
  // Unfinalization deletes/recreates payout rows, but the physical-check ledger
  // survives with a durable recon/recipient/type/sequence identity.
  try {
    const relinkedCount = await relinkPayoutPaymentAllocations(id);
    if (relinkedCount > 0) {
      log.info('Relinked payout payment allocations after re-finalize', { reconId: id, relinkedCount });
    }

    // Retire any snapshot left by the legacy preservation flow once the ledger
    // has successfully refreshed the recreated payout rows.
    if (recon.payment_snapshot) {
      await supabase
        .from('banker_recons')
        .update({ payment_snapshot: null })
        .eq('id', id);
    }
  } catch (paymentRelinkError) {
    log.error('Failed to relink payout payment allocations after re-finalize', { reconId: id }, paymentRelinkError as Error);
  }

  // Step 4: Auto-create servicing record (non-blocking)
  // Only if there are interim payments to service (first_payment_date < first_payment_to_investor)
  let createdServicingLoanId: string | undefined;
  // User-facing reason the servicing record was not linked (the goodbye letter depends on it).
  let servicingError: string | undefined;
  try {
    const result = await createFromBankerRecon(id);
    if (result.success) {
      log.info('Auto-created servicing loan on finalize', { reconId: id, servicingLoanId: result.servicingLoanId });
      createdServicingLoanId = result.servicingLoanId;

      // Pre-cache servicing form PDFs (non-blocking fire-and-forget)
      precacheServicingForms(recon.encompass_loan_id).catch((e) =>
        log.warn('Failed to pre-cache servicing forms', { reconId: id }, e as Error)
      );
    } else {
      log.info('Servicing not created on finalize', { reconId: id, reason: result.error });
      servicingError = result.error || 'The servicing record could not be set up.';
    }
  } catch (setupError) {
    // Non-blocking: log warning but don't fail finalization
    log.warn('Failed to auto-create servicing on finalize', { reconId: id }, setupError as Error);
    servicingError = 'The servicing record could not be set up.';
  }

  log.endTimer(timer, { success: true });
  return { success: true, servicingLoanId: createdServicingLoanId, servicingError, recon };
}

/**
 * Revert a finalized recon back to draft status and remove associated payouts
 * This is the inverse of finalizeRecon — deletes payouts first, then reverts status
 */
export async function unfinalizeRecon(
  id: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  const timer = log.startTimer('unfinalizeRecon');

  // Get the recon
  const recon = await getBankerRecon(id);
  if (!recon) {
    log.endTimer(timer, { success: false });
    return { success: false, error: 'Recon not found' };
  }

  if (recon.status !== 'finalized') {
    log.endTimer(timer, { success: false });
    return { success: false, error: `Cannot un-finalize from status: ${recon.status}` };
  }

  // Step 1: Recreate ordinary payouts only. A committed volume installment and
  // its actual payment links remain owed and attached during a reopen.
  // payout_payment_allocations uses ON DELETE SET NULL, so every check and
  // allocation remains auditable and can be relinked when payouts are recreated.
  const { error: deleteError } = await supabase
    .from('payouts')
    .delete()
    .eq('banker_recon_id', id)
    .neq('payout_type', 'volume_bonus');

  if (deleteError) {
    log.error('Failed to delete payouts during un-finalize', { id }, deleteError);
    log.endTimer(timer, { success: false });
    return { success: false, error: `Failed to remove payouts: ${deleteError.message}` };
  }

  // Step 1.5: Delete servicing loan linked to this recon (non-blocking)
  try {
    const { data: servicingLoan } = await supabase
      .from('servicing_loans')
      .select('id')
      .eq('banker_recon_id', id)
      .maybeSingle();
    if (servicingLoan) {
      await deleteServicingLoan(servicingLoan.id);
      log.info('Cleaned up servicing loan on un-finalize', { bankerReconId: id, servicingLoanId: servicingLoan.id });
    }
  } catch (e) {
    log.warn('Failed to clean up servicing loan on un-finalize', { bankerReconId: id }, e);
  }

  // Step 2: Revert status to draft
  const { error: updateError } = await supabase
    .from('banker_recons')
    .update({
      status: 'draft',
      finalized_by: null,
      finalized_at: null,
      updated_by: userId,
      updated_at: new Date().toISOString()
    })
    .eq('id', id);

  if (updateError) {
    log.error('Failed to revert recon status to draft', { id }, updateError);
    log.endTimer(timer, { success: false });
    return { success: false, error: updateError.message };
  }

  log.info('Un-finalized recon', { id, loanNumber: recon.loan_number });
  log.endTimer(timer, { success: true });
  return { success: true };
}

// ============================================================================
// USER LOOKUP
// ============================================================================

/**
 * Look up internal user ID by Encompass user ID
 * Encompasses loans store loan_officer_id as Encompass identifier, not our users.id
 */
export async function getUserIdByEncompassId(
  encompassUserId: string,
  organizationId: string
): Promise<string | null> {
  const timer = log.startTimer('getUserIdByEncompassId');

  const { data, error } = await supabase
    .from('users')
    .select('id')
    .eq('encompass_user_id', encompassUserId)
    .eq('organization_id', organizationId)
    .single();

  if (error && error.code !== 'PGRST116') {
    log.error('Failed to lookup user by encompass ID', { encompassUserId, organizationId }, error);
    log.endTimer(timer, { success: false });
    return null;
  }

  log.endTimer(timer, { success: true, found: !!data });
  return data?.id || null;
}

// ============================================================================
// COMPENSATION LOOKUP
// ============================================================================

/**
 * Get compensation settings from recruits table for a user
 * Uses migrated_to_user_id to link recruits → users (user_id column doesn't exist)
 */
export async function getCompensationSettings(
  userId: string
): Promise<{ banked_compensation: number | null; banked_override_bps: number | null; override_recipient_id: string | null; override_recipient_name: string | null } | null> {
  const timer = log.startTimer('getCompensationSettings');

  const { data, error } = await supabase
    .from('users')
    .select('banked_compensation, banked_override_bps, override_recipient_id, override_recipient:override_recipient_id(first_name, last_name)')
    .eq('id', userId)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    log.error('Failed to get compensation settings', { userId }, error);
    log.endTimer(timer, { success: false });
    // Fail closed: a read ERROR must be distinguishable from "user has no
    // settings" (null) — persisting after an unverified read could silently
    // drop the LO's real overrides/compensation.
    throw error;
  }

  if (!data) {
    log.endTimer(timer, { success: true, found: false });
    return null;
  }

  const row = data as CompensationSettingsRow;
  const recipient = row.override_recipient;
  const recipientName = recipient
    ? [recipient.first_name, recipient.last_name].filter(Boolean).join(' ') || null
    : null;
  log.endTimer(timer, { success: true, found: true });
  return {
    banked_compensation: row.banked_compensation,
    banked_override_bps: row.banked_override_bps,
    override_recipient_id: row.override_recipient_id,
    override_recipient_name: recipientName
  };
}

// ============================================================================
// SAVE DRAFT (FULL SAVE)
// ============================================================================

/**
 * Save or update a banker recon with all its line items
 * This is the main "Save Draft" function used by the UI
 */
export async function saveBankerReconDraft(
  organizationId: string,
  encompassLoanId: string,
  reconData: Omit<UpdateBankerReconInput, 'id'> & {
    loan_number: string;
    loan_amount: number;
    lo_user_id?: string;
    loan_snapshot?: Record<string, unknown>;
  },
  lineItems: Omit<CreateLineItemInput, 'banker_recon_id'>[],
  userId?: string,
  expectedUpdatedAt?: string | null
): Promise<{ recon: BankerRecon | null; lineItems: BankerReconLineItem[]; error?: string }> {
  const timer = log.startTimer('saveBankerReconDraft');

  if (readReconFeeAccounting(reconData.loan_snapshot)) {
    const { data, error } = await supabase.rpc('save_banker_recon_fee_draft', {
      p_organization_id: organizationId,
      p_encompass_loan_id: encompassLoanId,
      p_expected_updated_at: expectedUpdatedAt ?? null,
      p_header: reconData,
      p_items: lineItems
    });
    log.endTimer(timer, { metadata: { success: !error } });
    return error || !data?.recon
      ? { recon: null, lineItems: [], error: error?.message || 'Atomic fee save failed' }
      : { recon: data.recon, lineItems: data.lineItems };
  }

  // Check if recon already exists
  let recon = await getBankerReconByLoan(organizationId, encompassLoanId);

  if (readReconFeeAccounting(recon?.loan_snapshot)) {
    return { recon: null, lineItems: [], error: 'Reload this versioned fee draft before saving' };
  }

  if (recon) {
    // Frozen recons are never writable through the draft-save path — reject on
    // the AUTHORITATIVE status, not client state (which can transiently read
    // 'draft' while a loan is loading).
    if (recon.status !== 'draft') {
      log.warn('Rejected draft save against non-draft recon', { metadata: { id: recon.id, status: recon.status } });
      log.endTimer(timer, { metadata: { success: false } });
      return { recon: null, lineItems: [], error: 'Cannot modify a finalized reconciliation — unfinalize it first' };
    }
    // Conditional update: touches zero rows (and fails the save, before any line
    // items are replaced) if the recon was finalized between lookup and write.
    recon = await updateBankerRecon({
      id: recon.id,
      ...reconData
    }, userId, { requireStatus: 'draft' });
    if (!recon) {
      log.endTimer(timer, { metadata: { success: false } });
      return { recon: null, lineItems: [], error: 'Recon is no longer a draft — refresh and try again' };
    }
  } else {
    // Create new recon
    recon = await createBankerRecon({
      organization_id: organizationId,
      encompass_loan_id: encompassLoanId,
      loan_number: reconData.loan_number,
      loan_amount: reconData.loan_amount,
      lo_user_id: reconData.lo_user_id,
      closing_date: reconData.closing_date as string | undefined,
      funds_to_escrow_date: reconData.funds_to_escrow_date as string | undefined,
      funds_received_date: reconData.funds_received_date as string | undefined,
      loan_snapshot: reconData.loan_snapshot
    }, userId);

    // Update with additional fields (fresh inserts are drafts by definition;
    // the condition still guards against an impossibly fast finalize race)
    if (recon) {
      recon = await updateBankerRecon({
        id: recon.id,
        ...reconData
      }, userId, { requireStatus: 'draft' });
    }
  }

  if (!recon) {
    log.endTimer(timer, { success: false });
    return { recon: null, lineItems: [], error: 'Failed to save recon' };
  }

  // Save line items — an RPC failure (null) is a failed save REGARDLESS of item
  // count: clearing all rows + a failed replace must not report success while
  // the old rows persist and could be frozen by a finalize.
  const savedLineItems = await bulkUpsertLineItems(recon.id, lineItems);
  if (savedLineItems === null || (lineItems.length > 0 && savedLineItems.length === 0)) {
    log.error('Line item save failed', { reconId: recon.id, itemCount: lineItems.length });
    log.endTimer(timer, { success: false });
    return { recon: null, lineItems: [], error: 'Failed to save line items' };
  }

  // Refresh recon to get updated totals from trigger
  const refreshedRecon = await getBankerRecon(recon.id);

  log.info('Saved banker recon draft', { id: recon.id, lineItemCount: savedLineItems.length });
  log.endTimer(timer, { success: true });

  return { recon: refreshedRecon, lineItems: savedLineItems };
}

// ============================================================================
// INVESTOR CONDITIONS
// ============================================================================

// ============================================================================
// RECON DOCUMENT HELPERS
// ============================================================================

export interface ReconDocument {
  id: string;
  documentType: string;
  storagePath: string;
  fileName: string;
  createdAt: string;
}

/**
 * Batch-fetch loan_documents for a set of banker_recon_ids, grouped by recon ID.
 * Returns {} on empty input or query error (logs warning on failure).
 */
export async function getReconDocumentsByIds(
  reconIds: string[]
): Promise<Record<string, ReconDocument[]>> {
  if (reconIds.length === 0) {
    return {};
  }

  const { data, error } = await supabase
    .from('loan_documents')
    .select('id, banker_recon_id, document_type, source_file, original_filename, created_at')
    .in('banker_recon_id', reconIds)
    .in('document_type', ['warehouse_reconciliation', 'purchase_advice'])
    .order('created_at', { ascending: false });

  if (error) {
    log.warn('Failed to fetch recon documents for enrichment', { reconIds, error: error.message });
    return {};
  }

  const grouped: Record<string, ReconDocument[]> = {};
  for (const row of data || []) {
    const reconId = row.banker_recon_id as string;
    if (!grouped[reconId]) {
      grouped[reconId] = [];
    }
    grouped[reconId].push({
      id: row.id,
      documentType: row.document_type,
      storagePath: row.source_file,
      fileName: row.original_filename || row.source_file.split('/').pop() || 'Document',
      createdAt: row.created_at
    });
  }

  return grouped;
}

// ============================================================================
// AUTO-FILL FROM DOCUMENTS
// ============================================================================

/**
 * Build the stable, reprocess-sensitive review key for a queue row. Returns null when there's no
 * extracted text yet. `updated_at` is auto-bumped by the update_queue_updated_at trigger on every queue
 * write, so re-extraction changes the key and re-triggers the review modal.
 */
function buildReviewKey(
  queueId: string | null,
  queueUpdatedAt: string | null,
  extractedCharCount: number | null,
  hasText: boolean
): string | null {
  if (!queueId || !hasText) {
    return null;
  }
  return [queueId, queueUpdatedAt ?? '', extractedCharCount ?? 0, BANKER_EXTRACTOR_VERSION].join('|');
}

/**
 * Fetch the warehouse-reconciliation and purchase-advice documents for a loan (linked to the recon OR
 * unlinked — uploads aren't linked until the draft is saved), each joined to its latest extraction-queue
 * row. The extracted text lives in document_processing_queue.extracted_text (loan_documents.content is
 * NULL for these docs). Org-scoping is enforced by RLS on both tables.
 */
export async function getReconDocumentTexts(params: {
  encompassLoanId: string;
  reconId?: string | null;
}): Promise<ReconDocumentAutoFill[]> {
  const { encompassLoanId, reconId } = params;

  const { data: docs, error: docsError } = await supabase
    .from('loan_documents')
    .select('id, document_type, source_file, metadata, banker_recon_id, created_at')
    .eq('encompass_loan_id', encompassLoanId)
    .in('document_type', ['warehouse_reconciliation', 'purchase_advice'])
    .order('created_at', { ascending: false });

  if (docsError) {
    log.warn('getReconDocumentTexts: failed to load loan_documents', { encompassLoanId, error: docsError.message });
    return [];
  }

  // Linked to this recon OR unlinked; keep the newest doc per type (rows are created_at DESC).
  const relevant = (docs || []).filter(
    (d) => d.banker_recon_id === reconId || d.banker_recon_id === null
  );
  const newestByType = new Map<string, (typeof relevant)[number]>();
  for (const d of relevant) {
    if (!newestByType.has(d.document_type)) {
      newestByType.set(d.document_type, d);
    }
  }
  const selectedDocs = Array.from(newestByType.values());
  if (selectedDocs.length === 0) {
    return [];
  }

  // Latest queue row per document_id (rows ordered updated_at DESC).
  const docIds = selectedDocs.map((d) => d.id);
  const { data: queueRows, error: queueError } = await supabase
    .from('document_processing_queue')
    .select('id, document_id, extracted_text, status, extracted_char_count, updated_at')
    .in('document_id', docIds)
    .order('updated_at', { ascending: false });

  if (queueError) {
    log.warn('getReconDocumentTexts: failed to load queue rows', { encompassLoanId, error: queueError.message });
  }

  const latestQueueByDoc = new Map<string, NonNullable<typeof queueRows>[number]>();
  for (const q of queueRows || []) {
    if (!latestQueueByDoc.has(q.document_id)) {
      latestQueueByDoc.set(q.document_id, q);
    }
  }

  return selectedDocs.map((doc) => {
    const q = latestQueueByDoc.get(doc.id) || null;
    const extractedText = (q?.extracted_text as string | null) ?? null;
    const hasText = !!extractedText && extractedText.trim().length > 0;
    const queueId = (q?.id as string | null) ?? null;
    const queueUpdatedAt = (q?.updated_at as string | null) ?? null;
    const extractedCharCount = (q?.extracted_char_count as number | null) ?? null;
    const reviewKey = buildReviewKey(queueId, queueUpdatedAt, extractedCharCount, hasText);

    const metadata = (doc.metadata as Record<string, unknown> | null) || {};
    const reviewMark = (metadata.autoFillReview as AutoFillReviewMark | undefined) || null;
    const cache = (metadata.autoFillExtraction as AutoFillExtractionCache | undefined) || null;

    return {
      documentId: doc.id,
      documentType: doc.document_type as BankerDocumentType,
      extractedText,
      storagePath: (doc.source_file as string | null) ?? null,
      queueId,
      queueStatus: (q?.status as string | null) ?? null,
      queueUpdatedAt,
      extractedCharCount,
      reviewKey,
      alreadyReviewed: !!reviewKey && reviewMark?.reviewKey === reviewKey,
      // Only reuse a cache that matches the current review key.
      autoFillExtraction: cache && reviewKey && cache.reviewKey === reviewKey ? cache : null
    };
  });
}

/**
 * Capture the extraction → finalized-record diff into `banker_recon_autofill_feedback` (training data).
 *
 * Reads each linked doc's RAW `metadata.autoFillExtraction` DIRECTLY (not via getReconDocumentTexts — its
 * reviewKey filter hides caches once the extractor version bumps), diffs it against the finalized record per
 * the shared pure logic, and writes via the ownership-checked `replace_autofill_feedback` RPC (delete+replace
 * per recon/doc). Best-effort: callers wrap in try/catch; this never throws on the happy path.
 */
export async function captureAutoFillFeedback(reconId: string): Promise<void> {
  const { data: recon, error: reconErr } = await supabase
    .from('banker_recons')
    .select('id, encompass_loan_id, wire_from_investor, wire_from_warehouse')
    .eq('id', reconId)
    .single();
  if (reconErr || !recon) {
    log.warn('captureAutoFillFeedback: recon not found', { reconId });
    return;
  }

  // Scope to docs LINKED to THIS recon (not merely the same loan) so stale / other-recon docs on the same
  // loan can't pollute the feedback table. Matches finalizeRecon's own doc query + the RPC's ownership check.
  const { data: docs } = await supabase
    .from('loan_documents')
    .select('id, document_type, metadata')
    .eq('banker_recon_id', reconId)
    .in('document_type', ['warehouse_reconciliation', 'purchase_advice']);
  if (!docs || docs.length === 0) {
    return;
  }

  const lineItems = await getLineItems(reconId);
  const finalItems = buildFinalFeedbackItems(
    lineItems.map(li => ({ category: li.category, line_type: li.line_type, amount: li.amount })),
    recon.wire_from_investor as number | string | null,
    recon.wire_from_warehouse as number | string | null
  );

  for (const doc of docs) {
    const meta = (doc.metadata as Record<string, unknown> | null) || {};
    const cache = meta.autoFillExtraction as AutoFillExtractionCache | undefined;
    if (!cache || cache.status !== 'extracted') {
      continue;
    }
    const isPA = doc.document_type === 'purchase_advice';
    const extraction = flattenExtractionForFeedback(cache.fields, cache.fieldConfidence);
    const rows = diffExtractionVsFinal(extraction, finalItems, ownedMissedCategories(isPA));
    const payload = rows.map(r => ({
      ...r,
      document_type: doc.document_type,
      classified_as: cache.documentType ?? null,
      review_key: cache.reviewKey ?? null,
      extractor_version: cache.extractorVersion ?? null
    }));
    // Always call (even with []) so the delete-replace clears stale rows for this doc.
    const { error } = await supabase.rpc('replace_autofill_feedback', {
      p_recon_id: reconId,
      p_doc_id: doc.id,
      p_rows: payload
    });
    if (error) {
      log.warn('replace_autofill_feedback failed', { reconId, docId: doc.id }, error);
    }
  }
}

/**
 * Atomically merge the autoFillExtraction and/or autoFillReview keys into a document's metadata via the
 * mark_document_autofill RPC (whitelisted keys, SECURITY INVOKER under loan_documents RLS). Pass only the
 * key(s) you want to write. Best-effort: logs and returns false on error.
 */
export async function markDocumentAutoFill(
  documentId: string,
  patch: { extraction?: AutoFillExtractionCache; review?: AutoFillReviewMark }
): Promise<boolean> {
  const { error } = await supabase.rpc('mark_document_autofill', {
    p_document_id: documentId,
    p_extraction: patch.extraction ?? null,
    p_review: patch.review ?? null
  });
  if (error) {
    log.warn('markDocumentAutoFill failed', { documentId, error: error.message });
    return false;
  }
  return true;
}

// ============================================================================
// DOC ORDER RECON FEED (pull-at-build)
// ============================================================================

/**
 * One banker-recon revenue row derived from a completed Doc Order.
 * `lineType` is a deterministic key so rebuilds are idempotent (no duplicates).
 */
export interface DocOrderReconRow {
  lineType: string;
  description: string;
  amount: number;
  reimbursable: boolean;
  nets_to_warehouse_advance?: boolean;
  fee_source?: ReconFeeSource;
}

export interface DocOrderReconFeed {
  /** False when a read failed or no order was visible; preserve existing rows. */
  authoritative: boolean;
  rows: DocOrderReconRow[];
}

/** Split portion shape as stored in `doc_order_fees.splits` jsonb (camelCase keys). */
type DocOrderFeeSplitRow = {
  amount?: number | string | null;
  paidBy?: string | null;
  reimburse?: boolean | null;
};

/** Subset of `doc_order_fees` columns the recon feed reads (snake_case columns). */
type DocOrderFeeRow = {
  fee_key: string | null;
  name: string | null;
  amount: number | string | null;
  paid_by: string | null;
  reimburse: boolean | null;
  not_applicable: boolean | null;
  source: string | null;
  splits: DocOrderFeeSplitRow[] | null;
};

/**
 * Pure split-aware builder — mirrors the Doc Order LO summary reimbursement rule
 * (`_shared/doc-order-core/reimburse.ts`). Emits exactly three kinds of rows:
 *   (a) LO-paid reimbursements (reimbursable LO revenue),
 *   (b) Lendwise-paid reimbursements (non-reimbursable company revenue), and
 *   (c) manual doc-order fees not already emitted (charged extras).
 * NEVER emits E&O/LOCR/profitability/lender-credit rows — the recon owns those.
 */
function buildDocOrderReconRows(fees: DocOrderFeeRow[]): DocOrderReconRow[] {
  const rows: DocOrderReconRow[] = [];

  for (const fee of fees) {
    const amount = Number(fee.amount) || 0;
    if (!(amount > 0) || fee.not_applicable) {
      continue;
    }

    const feeKey = fee.fee_key ?? '';
    const feeName = fee.name ?? '';
    let emittedReimbursement = false;

    const splits = Array.isArray(fee.splits) ? fee.splits : null;
    if (splits && splits.length >= 2) {
      splits.forEach((portion, idx) => {
        const portionAmount = Number(portion?.amount) || 0;
        const paidBy = portion?.paidBy;
        if (
          portion?.reimburse === true &&
          portionAmount > 0 &&
          (paidBy === 'LO' || paidBy === 'Lendwise')
        ) {
          // Every portion is a numbered split, 1-based — the parent row carries
          // no portion identity (split-model revision, Jennifer bug pass 10/11;
          // matches doc-order-core/reimburse.ts). Keys stay idx-based.
          const baseName = `${feeName} · Split ${idx + 1}`;
          const loReimbursement = paidBy === 'LO';
          rows.push({
            lineType: loReimbursement
              ? `doc_order_reimb_${feeKey}_${idx}`
              : `doc_order_lendwise_reimb_${feeKey}_${idx}`,
            description: `${baseName} (${loReimbursement ? 'LO' : 'Lendwise'} reimbursement)`,
            amount: portionAmount,
            reimbursable: loReimbursement
          });
          emittedReimbursement = true;
        }
      });
    } else if (
      fee.reimburse === true &&
      (fee.paid_by === 'LO' || fee.paid_by === 'Lendwise')
    ) {
      const loReimbursement = fee.paid_by === 'LO';
      rows.push({
        lineType: loReimbursement ? `doc_order_reimb_${feeKey}` : `doc_order_lendwise_reimb_${feeKey}`,
        description: `${feeName} (${loReimbursement ? 'LO' : 'Lendwise'} reimbursement)`,
        amount,
        reimbursable: loReimbursement
      });
      emittedReimbursement = true;
    }

    // Manual doc-order fees are charged extras — but only when not already
    // surfaced as a reimbursement (avoids double-counting the same fee).
    if (!emittedReimbursement && fee.source === 'manual') {
      rows.push({
        lineType: `doc_order_fee_${feeKey}`,
        description: feeName,
        amount,
        reimbursable: false
      });
    }
  }

  return rows;
}

/**
 * Pull-at-build recon feed: the latest non-cancelled Doc Order contributes rows
 * only while it is complete. A reopened latest sequence must not expose an older
 * completed sequence's figures while those figures are under revision. Reads by
 * `doc_orders.loan_id` (the internal `encompass_loans.id`). The authoritative
 * bit distinguishes an explicit non-complete latest order from a failed/hidden
 * read so the caller never deletes accounting rows on uncertainty.
 */
export async function getDocOrderReconFeed(loanId: string): Promise<DocOrderReconFeed> {
  const { data: order, error: orderError } = await supabase
    .from('doc_orders')
    .select('id, stage')
    .eq('loan_id', loanId)
    .is('cancelled_at', null)
    .order('sequence', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (orderError) {
    log.error('Failed to load completed doc order for recon feed', {
      metadata: { loanId, error: orderError.message }
    });
    return { authoritative: false, rows: [] };
  }

  // A null row can also mean RLS hid Doc Orders from this recon viewer; that
  // is not enough evidence to delete saved accounting rows. An explicitly
  // visible latest order in a non-complete stage is authoritative.
  if (!order) {
    return { authoritative: false, rows: [] };
  }
  if (order.stage !== 'complete') {
    return { authoritative: true, rows: [] };
  }

  const { data: fees, error: feesError } = await supabase
    .from('doc_order_fees')
    .select('fee_key, name, amount, paid_by, reimburse, not_applicable, source, splits')
    .eq('doc_order_id', order.id)
    .order('display_order');

  if (feesError) {
    log.error('Failed to load doc order fees for recon feed', {
      metadata: { loanId, orderId: order.id, error: feesError.message }
    });
    return { authoritative: false, rows: [] };
  }

  return {
    authoritative: true,
    rows: buildDocOrderReconRows((fees ?? []) as DocOrderFeeRow[])
  };
}

/** Compatibility wrapper for non-stateful consumers and focused unit tests. */
export async function getCompletedDocOrderReconRows(loanId: string): Promise<DocOrderReconRow[]> {
  return (await getDocOrderReconFeed(loanId)).rows;
}

// ============================================================================
// EXPORTS
// ============================================================================

export const bankerReconService = {
  // Recon CRUD
  getBankerRecon,
  getBankerReconByLoan,
  getCanonicalFhaUfmip,
  listBankerRecons,
  createBankerRecon,
  updateBankerRecon,
  deleteBankerRecon,

  // Line items CRUD
  getLineItems,
  createLineItem,
  updateLineItem,
  deleteLineItem,
  bulkUpsertLineItems,

  // Status transitions
  finalizeRecon,
  unfinalizeRecon,

  // User lookup
  getUserIdByEncompassId,

  // Compensation
  getCompensationSettings,

  // Full save
  saveBankerReconDraft,

  // Search exclusions
  getFinalizedLoanIds,

  // Recon document helpers
  getReconDocumentsByIds,

  // Doc Order recon feed
  getDocOrderReconFeed,
  getCompletedDocOrderReconRows,

  // Auto-fill from documents
  getReconDocumentTexts,
  markDocumentAutoFill,

  // Revenue report
  getRevenueReportData
};

export default bankerReconService;

// ============================================================================
// REVENUE REPORT
// ============================================================================

export type { RevenueReportLoan } from '@/utils/banker-recon/revenueReportProjection';

/**
 * Fetch finalized/pushed recons for the revenue report with wire decomposition.
 * Sources display data from loan_snapshot (historical) and users join (LO name).
 */
export async function getRevenueReportData(
  organizationId: string,
  dateRange: { from: string; to: string }
): Promise<RevenueReportLoan[]> {
  const timer = log.startTimer('getRevenueReportData');

  // Query 1: Recons with LO join
  const { data: recons, error: reconsError } = await supabase
    .from('banker_recons')
    .select('*, lo:lo_user_id(first_name, last_name)')
    .eq('organization_id', organizationId)
    .in('status', ['finalized', 'pushed'])
    .gte('investor_purchase_date', dateRange.from)
    .lte('investor_purchase_date', dateRange.to)
    .order('investor_purchase_date', { ascending: false });

  if (reconsError || !recons?.length) {
    if (reconsError) {
      log.error('Failed to fetch recons for revenue report', { organizationId }, reconsError);
    }
    log.endTimer(timer, { success: !reconsError, count: 0 });
    return [];
  }

  // Query 2: Line items for all returned recons
  const reconIds = recons.map((r: BankerRecon) => r.id);
  const { data: lineItems, error: liError } = await supabase
    .from('banker_recon_line_items')
    .select('*')
    .in('banker_recon_id', reconIds)
    .order('category')
    .order('display_order');

  if (liError) {
    log.error('Failed to fetch line items for revenue report', { reconIds: reconIds.length }, liError);
    log.endTimer(timer, { success: false });
    return [];
  }

  // Group line items by recon ID
  const lineItemsByRecon = new Map<string, BankerReconLineItem[]>();
  for (const li of (lineItems || []) as BankerReconLineItem[]) {
    const arr = lineItemsByRecon.get(li.banker_recon_id) || [];
    arr.push(li);
    lineItemsByRecon.set(li.banker_recon_id, arr);
  }

  // Process each recon
  const results: RevenueReportLoan[] = [];
  for (const recon of recons as (BankerRecon & { lo?: { first_name: string; last_name: string } | null })[]) {
    const items = lineItemsByRecon.get(recon.id) || [];
    results.push(buildRevenueReportLoanProjection(recon, items));
  }

  log.endTimer(timer, { success: true, count: results.length });
  return results;
}
